/**
 * usePerTabValue / usePerTabFlag — a value remembered PER TAB (keyed by the
 * active tab id) and persisted to localStorage so it also survives reloads.
 *
 * The returned value mirrors the ACTIVE tab's stored choice; setting writes back
 * to that tab only. A tab with no stored choice falls back to `defaultValue` —
 * so e.g. the right details panel can be open on the overview board yet closed
 * in a file editor, or each editor tab can keep its own editor/board layout,
 * remembered independently.
 */
import { useCallback, useEffect, useState } from "react";
import { NON_FILE_TAB_NS } from "../workbench/tabs.js";
import type { WorkspaceEvent } from "../workbench/WorkspaceStore.js";

export type Updater<T> = T | ((prev: T) => T);
export type FlagUpdater = Updater<boolean>;

/** The slice of the workspace store this hook needs: the event channel that
 *  broadcasts rename id maps. Structural so tests can stub it. */
export interface PerTabEventSource {
  on(listener: (ev: WorkspaceEvent) => void): () => void;
}

/** Legacy (pre-namespace) key shapes that belonged to NON-file tabs. */
const LEGACY_NON_FILE = /^(doc|types|board:|process:|view:|module:|custom:)/;
/** Suffix for the per-storage-key "already migrated" marker entry. */
const MIGRATED_SUFFIX = ".nsMigrated";
import { hostStorage } from "../host/hostStorage.js";

function load<T>(storageKey: string): Record<string, T> {
  try {
    const raw = hostStorage.getItem(storageKey);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    // Valid JSON of the wrong TYPE (a scalar/array at this key) must degrade
    // like unparsable JSON — `"x" in <primitive>` throws in render, which
    // white-screened every mount until the key was hand-deleted (r29).
    const map =
      parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, T>)
        : {};
    if (hostStorage.getItem(storageKey + MIGRATED_SUFFIX)) return map;
    // ONE-SHOT migration of pre-namespace data: non-file tab prefs were
    // stored under the unprefixed id ("doc", "board:__root__", …) — COPY
    // them to the NUL-prefixed canonical keys. Copy, not move: in the old id
    // scheme a file literally named "doc" SHARED its key with the doc
    // singleton, so the legacy value is legitimately both tabs' inheritance
    // — each diverges independently from here. (A read-time fallback instead
    // of this migration false-positived forever: a CURRENT-build file named
    // "doc" writes the unprefixed key fresh, and the doc singleton kept
    // inheriting it.) The migrated map persists via the normal save effect.
    for (const [k, v] of Object.entries(map)) {
      if (!k.startsWith(NON_FILE_TAB_NS) && LEGACY_NON_FILE.test(k)) {
        const canonical = `${NON_FILE_TAB_NS}${k}`;
        if (!(canonical in map)) map[canonical] = v;
      }
    }
    hostStorage.setItem(storageKey + MIGRATED_SUFFIX, "1");
    return map;
  } catch {
    return {};
  }
}

export function usePerTabValue<T>(
  activeTabId: string | null,
  storageKey: string,
  defaultValue: T,
  events?: PerTabEventSource,
): readonly [T, (next: Updater<T>) => void] {
  const [byTab, setByTab] = useState<Record<string, T>>(() => load<T>(storageKey));

  useEffect(() => {
    hostStorage.setItem(storageKey, JSON.stringify(byTab));
  }, [storageKey, byTab]);

  // A file tab's id IS its path — a rename re-keys the SAME logical tab.
  // Follow it via the exact id map the store broadcast (the same contract
  // EditModeProvider and the store's own view records honor), else the
  // renamed tab's remembered choice silently resets to the default and the
  // old key lingers in storage forever.
  useEffect(() => {
    if (!events) return;
    return events.on((ev) => {
      if (ev.type !== "fileRenamed" || ev.renamedTabIds.length === 0) return;
      const map = new Map(ev.renamedTabIds);
      setByTab((prev) => {
        let changed = false;
        const next: Record<string, T> = {};
        const moved: Array<[string, T]> = [];
        for (const [id, v] of Object.entries(prev)) {
          const dest = map.get(id);
          if (dest !== undefined) {
            moved.push([dest, v as T]);
            changed = true;
          } else {
            next[id] = v as T;
          }
        }
        // Second phase: migrated LIVE tabs win over any stale same-key entry.
        // Unlike the store's view records / EditModeProvider, this map never
        // prunes dead tab ids — so the rename destination can hold a
        // previously-deleted file's leftover (exactly the condition making
        // the rename legal), and iteration order must not decide the merge.
        for (const [dest, v] of moved) next[dest] = v;
        return changed ? next : prev;
      });
    });
  }, [events]);

  const value = activeTabId && activeTabId in byTab ? byTab[activeTabId]! : defaultValue;

  const set = useCallback(
    (next: Updater<T>) => {
      if (!activeTabId) return;
      setByTab((prev) => {
        const cur = activeTabId in prev ? prev[activeTabId]! : defaultValue;
        const val = typeof next === "function" ? (next as (p: T) => T)(cur) : next;
        if (activeTabId in prev && Object.is(val, cur)) return prev;
        return { ...prev, [activeTabId]: val };
      });
    },
    [activeTabId, defaultValue],
  );

  return [value, set] as const;
}

/** Boolean specialisation — the common case (panel collapse, etc.). */
export function usePerTabFlag(
  activeTabId: string | null,
  storageKey: string,
  defaultValue: boolean,
  events?: PerTabEventSource,
): readonly [boolean, (next: FlagUpdater) => void] {
  return usePerTabValue<boolean>(activeTabId, storageKey, defaultValue, events);
}
